/**
 * RoomScoreGrid — responsive 6-card grid showing per-room scores.
 * Layout: 1-col mobile, 2-col sm, 3-col md+.
 * N/A rendered for null scores (e.g. stairs when stairsNa is true).
 */

import { Card, CardContent } from '@/shared/components/ui/card';
import { cn } from '@/shared/utils/cn';
import { ROOM_LABELS } from '../utils/roomMap';
import { getScoreColor, getScoreLabel } from '../utils/scoreColor';
import type { RoomArea, SessionResponseDto } from '../types/homeAudit.types';

type RoomScoreGridProps = {
  session: SessionResponseDto;
};

const ORDER: RoomArea[] = [
  'BEDROOM', 'BATHROOM', 'KITCHEN', 'LIVING_AREAS', 'ENTRY_EXTERIOR', 'STAIRS',
];

const scoreField = (session: SessionResponseDto, area: RoomArea): number | null => {
  switch (area) {
    case 'BEDROOM': return session.bedroomScore;
    case 'BATHROOM': return session.bathroomScore;
    case 'KITCHEN': return session.kitchenScore;
    case 'LIVING_AREAS': return session.livingAreasScore;
    case 'ENTRY_EXTERIOR': return session.entryExteriorScore;
    case 'STAIRS': return session.stairsScore;
  }
};

export const RoomScoreGrid = ({ session }: RoomScoreGridProps) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="room-score-grid">
    {ORDER.map((area) => {
      const score = scoreField(session, area);
      const isNa = score === null;
      return (
        <Card key={area} className={cn('p-4', isNa && 'opacity-80')}>
          <CardContent className="p-0 space-y-2">
            <p className="text-sm text-muted-foreground">{ROOM_LABELS[area]}</p>
            <p className={cn('text-2xl font-medium', getScoreColor(score))}>
              {isNa ? 'N/A' : `${score!.toFixed(1)}/10`}
            </p>
            <p className="text-xs text-muted-foreground">
              {isNa ? 'N/A — No stairs' : getScoreLabel(score)}
            </p>
          </CardContent>
        </Card>
      );
    })}
  </div>
);
